import React, { useMemo } from "react";
import moment from "moment";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { useSetupFeesDetail } from "./hooks/useSetupFeesDetail";
import Header from "../../../components/Header";
import { Table } from "../../../../../shared/Table/Table";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { formatCurrency } from "../../../../../utilities/general";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const SetupFeesDetail = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();

  const { data, isLoading, error } = useSetupFeesDetail({
    clinic_id:
      selectedClinic?.value?.value !== 0 ? [selectedClinic?.value?.value] : [],
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
  });

  const requestParams = {
    clinic_id:
      selectedClinic?.value?.value !== 0 ? [selectedClinic?.value?.value] : [],
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
  };

  const { report = [], clinics: apiClinics = [] } = data || {};

  const tableColumns = [
    {
      data: "Patient Name",
      accessor: "patientName",
    },
    {
      data: "Email",
      accessor: "email",
    },
    {
      data: "Phone",
      accessor: "phone",
    },
    {
      data: "Membership Type",
      accessor: "membershipType",
    },
    {
      data: "Setup Fee",
      accessor: "setupFee",
    },
    {
      data: "Signup Date",
      accessor: "signupDate",
    },
    {
      data: "Payment Method",
      accessor: "paymentMethod",
    },
    {
      data: "Invoice #",
      accessor: "invoiceNumber",
    },
    {
      data: "Clinic",
      accessor: "clinicName",
    },
    {
      data: "Provider",
      accessor: "providerName",
    },
    {
      data: "Status",
      accessor: "status",
    },
  ];

  const tableData = useMemo(() => {
    return report.map((item) => ({
      key: item.patient_id,
      patientName: item.patient_name || "-",
      email: item.email || "-",
      phone: item.phone || "-",
      membershipType: item.membership_type || "-",
      setupFee: formatCurrency(item.setup_fee || 0),
      signupDate: item.signup_date
        ? moment(item.signup_date).format("MM/DD/YYYY")
        : "-",
      paymentMethod: item.payment_method || "-",
      invoiceNumber: item.invoice_number || "-",
      clinicName: item.clinic_name || "-",
      providerName: item.provider_name || "-",
      status: item.status || "-",
    }));
  }, [report]);

  const clinics = useMemo(() => {
    const clinicOptions = [{ label: "All Clinics", value: 0 }];
    if (apiClinics?.length) {
      clinicOptions.push(
        ...apiClinics.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      );
    }
    return clinicOptions;
  }, [apiClinics]);

  return (
    <div className="flex-col gap-16">
      <Header
        title="Setup Fees Detail"
        clinics={clinics}
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getSetupFeesDetail()}
        requestParams={requestParams}
      />

      <Box>
        {isLoading && (
          <div className={classes.tableContainer}>
            <Skeleton count={10} height={40} borderRadius="0px" />
          </div>
        )}

        {error && (
          <Empty
            position="center"
            title="API Error"
            description={`Failed to load setup fees data: ${
              error.message || "Unknown error"
            }`}
          />
        )}

        {!isLoading && !error && tableData.length > 0 && (
          <div className={classes.tableContainer} style={{ overflowX: "auto" }}>
            <Table
              cols={tableColumns}
              data={tableData}
              colClassName="bordersides"
              headColClassName={classes.tableHeaderColumn}
            />
          </div>
        )}

        {!isLoading && !error && tableData.length === 0 && (
          <Empty
            position="center"
            title="No setup fees data found"
            description="Try adjusting your date range or clinic selection"
          />
        )}
      </Box>
    </div>
  );
};

export default SetupFeesDetail;
